import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Pagination,
  IconButton,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ProfilePhotosApi from "../../api_calls/ProfilePhotosApi";
import ToggleProfilePhotoFlaggedApi from "../../api_calls/ToggleProfilePhotoFlaggedApi";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import Configs from "../../utils/Configs";

const PHOTOS_PER_PAGE = 12;
const BASE_URL = Configs.baseUrl;

const PhotoCard = ({ photo, onFlag }) => {
  const isValidPhoto =
    photo.profile_pic && !photo.profile_pic.includes("/media/none");

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        boxShadow: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          paddingTop: "66.66%",
          overflow: "hidden",
        }}
      >
        {isValidPhoto ? (
          <Zoom>
            <img
              src={`${BASE_URL}${photo.profile_pic}`}
              alt={`${photo.first_name} ${photo.other_names}`}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "zoom-in",
              }}
            />
          </Zoom>
        ) : (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#f0f0f0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#777",
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
            No Photo Available
          </Box>
        )}
      </Box>

      <CardContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          px: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexGrow: 1,
          }}
        >
          {photo.first_name} {photo.other_names}
        </Typography>
        <IconButton
          size="small"
          aria-label="Flag photo"
          onClick={() => isValidPhoto && onFlag(photo.user_id)}
          disabled={!isValidPhoto}
        >
          <FlagIcon
            fontSize="small"
            sx={{
              color:
                isValidPhoto && photo.profile_pic_flagged === "yes"
                  ? "red"
                  : "inherit",
              opacity: isValidPhoto ? 1 : 0.3,
            }}
          />
        </IconButton>
      </CardContent>
    </Card>
  );
};

const ProfilePhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPhotos(page);
  }, [page]);

  const fetchPhotos = async (pageNumber) => {
    setLoading(true);
    try {
      const response = await ProfilePhotosApi.fetchPhotos(
        pageNumber,
        PHOTOS_PER_PAGE
      );
      if (response.status === "success" && Array.isArray(response.data)) {
        // console.log("Fetched photos:", response.data);
        setPhotos(response.data);
        setTotalPages(response.total_pages || 1);
      } else {
        setPhotos([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("[fetchPhotos] Error:", error);
      setPhotos([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_, newPage) => {
    if (newPage !== page) {
      setPage(newPage);
    }
  };

  const handleFlagClick = async (userId) => {
    console.log(
      "[handleFlagClick] Toggling flagged status for user_id:",
      userId
    );
    try {
      const result = await ToggleProfilePhotoFlaggedApi.togglePhotoFlagged(
        userId
      );
      const { data, status } = result || {};
      if (status === "success" && data?.user_id) {
        setPhotos((prevPhotos) =>
          prevPhotos.map((photo) =>
            photo.user_id === data.user_id
              ? {
                  ...photo,
                  profile_pic_flagged: data.profile_pic_flagged,
                }
              : photo
          )
        );
      } else {
        console.error("[handleFlagClick] Invalid response:", result);
      }
    } catch (err) {
      console.error("[handleFlagClick] Exception:", err);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography
        fontWeight={600}
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PhotoLibraryIcon fontSize="small" />
        Profile Photos
      </Typography>

      <Grid container spacing={2}>
        {photos.map((photo) => (
          <Grid item xs={6} sm={4} md={2} key={photo.user_id}>
            <PhotoCard photo={photo} onFlag={handleFlagClick} />
          </Grid>
        ))}
      </Grid>

      {loading && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
            pb: 4,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default ProfilePhotos;
